import React, { useState, useEffect, useMemo } from 'react';
import {
  ShoppingCart, Search, Filter, ChevronDown, ChevronRight, ExternalLink,
  Bookmark, DollarSign, BookOpen, Loader2, Tag, Store, Lightbulb, X
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '';

const CATEGORY_META: Record<string, { label: string; color: string; icon: string }> = {
  it: { label: 'IT & Technologia', color: 'bg-blue-100 text-blue-700', icon: '💻' },
  biuro: { label: 'Biuro & Meble', color: 'bg-amber-100 text-amber-700', icon: '🪑' },
  marketing: { label: 'Marketing & Reklama', color: 'bg-pink-100 text-pink-700', icon: '📢' },
  uslugi: { label: 'Usługi', color: 'bg-emerald-100 text-emerald-700', icon: '🤝' },
  produkcja: { label: 'Produkcja & Narzędzia', color: 'bg-orange-100 text-orange-700', icon: '🔧' },
  transport: { label: 'Transport', color: 'bg-indigo-100 text-indigo-700', icon: '🚗' },
  wyposazenie: { label: 'Wyposażenie lokalu', color: 'bg-teal-100 text-teal-700', icon: '🏢' },
};

const PROGRAM_LABELS: Record<string, string> = {
  pup_grant: 'Dotacja PUP',
  wsparcie_na_starcie: 'Wsparcie na Starcie',
  dotacje_rozwojowe: 'Dotacje Rozwojowe',
  granty_fpn: 'Granty FPN',
  kfs: 'KFS Szkolenia',
};

interface CatalogItem {
  id: number;
  category: string;
  subcategory: string | null;
  item_name: string;
  description: string | null;
  price_min: number | null;
  price_max: number | null;
  price_typical: number | null;
  eligible_programs: string[] | null;
  where_to_buy: string[] | null;
  offer_tips: string | null;
  specifications: string | null;
  is_popular: boolean;
}

interface OfferInstruction {
  id: number;
  program_code: string;
  title: string;
  content: string;
  category: string;
}

interface Props {
  programFilter?: string; // pre-filter by program code
  onSelectItem?: (item: CatalogItem) => void; // callback when user selects an item
}

export const EquipmentCatalogPanel: React.FC<Props> = ({ programFilter, onSelectItem }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [instructions, setInstructions] = useState<OfferInstruction[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [expandedItem, setExpandedItem] = useState<number | null>(null);
  const [activeView, setActiveView] = useState<'catalog' | 'instructions'>('catalog');
  const [expandedInstruction, setExpandedInstruction] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (programFilter) params.set('program', programFilter);

        const [itemsRes, instrRes] = await Promise.all([
          fetch(`${API_BASE}/api/equipment-catalog?${params}`),
          fetch(`${API_BASE}/api/equipment-catalog/instructions?program=${programFilter || ''}`),
        ]);

        if (itemsRes.ok) setItems(await itemsRes.json());
        if (instrRes.ok) setInstructions(await instrRes.json());
      } catch (e) {
        console.error('Failed to load catalog:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [programFilter]);

  const filtered = useMemo(() => {
    let result = items;
    if (categoryFilter) result = result.filter(i => i.category === categoryFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(i =>
        i.item_name.toLowerCase().includes(q) ||
        (i.description || '').toLowerCase().includes(q) ||
        (i.subcategory || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [items, categoryFilter, search]);

  const categories = useMemo(() => {
    const cats = new Map<string, number>();
    items.forEach(i => cats.set(i.category, (cats.get(i.category) || 0) + 1));
    return Array.from(cats.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Collapsible Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full bg-gradient-to-r from-violet-50 to-blue-50 px-6 py-4 border-b flex items-center justify-between hover:from-violet-100 hover:to-blue-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ShoppingCart size={22} className="text-violet-600" />
          <h3 className="text-lg font-bold text-gray-900">Katalog sprzętu & Instrukcje ofertowe</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{items.length} pozycji</span>
          {collapsed ? <ChevronRight size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
        </div>
      </button>

      {loading && !collapsed && (
        <div className="p-6 flex items-center justify-center gap-2 text-gray-400">
          <Loader2 size={18} className="animate-spin" />
          Ładowanie katalogu...
        </div>
      )}

      {!collapsed && !loading && (
      <>

      {/* Tabs: Catalog / Instructions */}
      <div className="flex border-b bg-gray-50">
        <button
          onClick={() => setActiveView('catalog')}
          className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium border-b-2 transition-all ${
            activeView === 'catalog' ? 'text-violet-600 border-violet-500 bg-white' : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <ShoppingCart size={16} />
          Katalog sprzętu ({items.length})
        </button>
        <button
          onClick={() => setActiveView('instructions')}
          className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium border-b-2 transition-all ${
            activeView === 'instructions' ? 'text-emerald-600 border-emerald-500 bg-white' : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <BookOpen size={16} />
          Instrukcje ofertowe ({instructions.length})
        </button>
      </div>

      {/* ===== CATALOG VIEW ===== */}
      {activeView === 'catalog' && (
        <div className="p-4">
          {/* Search + filter */}
          <div className="flex gap-3 mb-4">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Szukaj sprzętu..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={14} />
                </button>
              )}
            </div>
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500"
            >
              <option value="">Wszystkie kategorie</option>
              {categories.map(([cat, count]) => (
                <option key={cat} value={cat}>
                  {CATEGORY_META[cat]?.label || cat} ({count})
                </option>
              ))}
            </select>
          </div>

          {/* Category chips (if no filter) */}
          {!categoryFilter && !search && (
            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map(([cat, count]) => {
                const meta = CATEGORY_META[cat] || { label: cat, color: 'bg-gray-100 text-gray-700', icon: '📦' };
                return (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    className={`${meta.color} px-3 py-1.5 rounded-full text-xs font-medium hover:opacity-80 transition-opacity`}
                  >
                    {meta.icon} {meta.label} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Items list */}
          <div className="space-y-2">
            {filtered.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                Brak wyników{search ? ` dla "${search}"` : ''}
              </div>
            ) : (
              filtered.map(item => {
                const expanded = expandedItem === item.id;
                const meta = CATEGORY_META[item.category] || { label: item.category, color: 'bg-gray-100 text-gray-700', icon: '📦' };
                return (
                  <div key={item.id} className="border rounded-lg overflow-hidden hover:border-violet-300 transition-colors">
                    {/* Item header */}
                    <button
                      onClick={() => setExpandedItem(expanded ? null : item.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <span className="text-lg">{meta.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-gray-900">{item.item_name}</span>
                          {item.is_popular && (
                            <span className="bg-yellow-100 text-yellow-700 text-[10px] px-1.5 py-0.5 rounded font-medium">Popularne</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{item.subcategory || item.category}</div>
                      </div>
                      {/* Price range */}
                      {item.price_typical && (
                        <div className="text-right shrink-0">
                          <div className="text-sm font-bold text-gray-900">~{item.price_typical.toLocaleString('pl-PL')} zł</div>
                          {item.price_min && item.price_max && (
                            <div className="text-[10px] text-gray-400">
                              {item.price_min.toLocaleString('pl-PL')} – {item.price_max.toLocaleString('pl-PL')} zł
                            </div>
                          )}
                        </div>
                      )}
                      {expanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                    </button>

                    {/* Expanded details */}
                    {expanded && (
                      <div className="px-4 pb-4 border-t bg-gray-50 space-y-3">
                        {/* Description */}
                        {item.description && (
                          <p className="text-sm text-gray-700 pt-3">{item.description}</p>
                        )}

                        {/* Specifications */}
                        {item.specifications && (
                          <div className="bg-blue-50 rounded-lg p-3">
                            <div className="text-xs font-medium text-blue-700 mb-1 flex items-center gap-1">
                              <Tag size={12} /> Specyfikacja
                            </div>
                            <p className="text-xs text-blue-600">{item.specifications}</p>
                          </div>
                        )}

                        {/* Offer tips */}
                        {item.offer_tips && (
                          <div className="bg-amber-50 rounded-lg p-3">
                            <div className="text-xs font-medium text-amber-700 mb-1 flex items-center gap-1">
                              <Lightbulb size={12} /> Jak pozyskać ofertę
                            </div>
                            <p className="text-xs text-amber-600">{item.offer_tips}</p>
                          </div>
                        )}

                        {/* Where to buy */}
                        {item.where_to_buy && item.where_to_buy.length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <Store size={12} className="text-gray-400" />
                            <span className="text-xs text-gray-500">Gdzie kupić:</span>
                            {item.where_to_buy.map(store => (
                              <span key={store} className="bg-white border rounded px-2 py-0.5 text-xs text-gray-600">{store}</span>
                            ))}
                          </div>
                        )}

                        {/* Eligible programs */}
                        {item.eligible_programs && item.eligible_programs.length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <Bookmark size={12} className="text-gray-400" />
                            <span className="text-xs text-gray-500">Programy:</span>
                            {item.eligible_programs.map(prog => (
                              <span key={prog} className="bg-violet-50 text-violet-600 rounded px-2 py-0.5 text-[10px] font-medium">
                                {PROGRAM_LABELS[prog] || prog}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Select button */}
                        {onSelectItem && (
                          <button
                            onClick={() => onSelectItem(item)}
                            className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-medium hover:bg-violet-700 transition-colors"
                          >
                            <ShoppingCart size={12} />
                            Dodaj do wniosku
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ===== INSTRUCTIONS VIEW ===== */}
      {activeView === 'instructions' && (
        <div className="p-4 space-y-3">
          {instructions.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">Brak instrukcji</div>
          ) : (
            instructions.map(instr => {
              const expanded = expandedInstruction === instr.id;
              return (
                <div key={instr.id} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedInstruction(expanded ? null : instr.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                  >
                    <BookOpen size={16} className="text-emerald-500 shrink-0" />
                    <span className="font-medium text-sm text-gray-900 flex-1">{instr.title}</span>
                    <span className="text-[10px] text-gray-400 shrink-0">
                      {instr.program_code === 'all' ? 'Ogólne' : PROGRAM_LABELS[instr.program_code] || instr.program_code}
                    </span>
                    {expanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                  </button>
                  {expanded && (
                    <div className="px-4 pb-4 border-t bg-gray-50">
                      <div className="prose prose-sm max-w-none pt-3 text-gray-700 text-xs leading-relaxed whitespace-pre-wrap">
                        {instr.content}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
};
